import { clamp } from "lodash-es";
import { onScopeDispose, ref } from "vue";

export interface ColumnResizeOptions {
  // px width per column at the moment the first drag starts
  measure: () => number[];
  minWidth?: number;
}

// Ephemeral, boundary-style column resizing: dragging the divider between two
// columns trades width between them, so the table's total width never changes.
// Widths live only in this composable's owner — nothing is persisted.
export function useColumnResize({ measure, minWidth = 48 }: ColumnResizeOptions) {
  const widths = ref<number[] | null>(null);
  let detach: (() => void) | null = null;

  onScopeDispose(() => detach?.(), true);

  function startResize(boundary: number, event: PointerEvent) {
    detach?.();
    const start = widths.value ?? measure();
    const pair = start[boundary] + start[boundary + 1];
    if (pair < 2 * minWidth) return;
    widths.value = start;
    const startX = event.clientX;

    function onMove(move: PointerEvent) {
      const left = clamp(start[boundary] + move.clientX - startX, minWidth, pair - minWidth);
      const next = [...start];
      next[boundary] = left;
      next[boundary + 1] = pair - left;
      widths.value = next;
    }
    function onEnd() {
      detach?.();
      detach = null;
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onEnd);
    document.addEventListener("pointercancel", onEnd);
    detach = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onEnd);
      document.removeEventListener("pointercancel", onEnd);
    };
  }

  return { widths, startResize };
}
